import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {Chat, ChatMessage, ChatMessageCreate, Document, GetParams, User} from '@app/shared';
import {NzDrawerService} from 'ng-zorro-antd/drawer';
import {ProjectUtilsService} from '../../../features/project/services/project-utils.service';
import {DocDrawerComponent} from '../doc-drawer/doc-drawer.component';
import {AuthService} from '../../../core/services/auth.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CommunicationService, escapeHtml, getSafeImageUrl, isExpirationAlert, PartnerService, ScreenService} from '@app/core';
import {BehaviorSubject, combineLatest, finalize, Subject, take, takeUntil} from 'rxjs';
import {DocModalComponent} from '../doc-modal/doc-modal.component';
import {NzModalService} from 'ng-zorro-antd/modal';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import {GlobalValuesService} from '../../../core/services/global-values.service';

@Component({
  selector: 'app-chat',
  standalone: false,

  templateUrl: './chat.component.html',
  styleUrl: './chat.component.less'
})
export class ChatComponent implements OnInit, OnDestroy {

  getSafeImageUrl = getSafeImageUrl

  @ViewChild('messageContainer') messageContainer!: ElementRef;
  @ViewChild('divider', {read: ElementRef}) dividerEl!: ElementRef;
  unreadedMessages: number = 0;
  instantMessagesUnread: number = 0;
  unreadIndex: number | undefined;
  @Output() isDocDrawerOpen: EventEmitter<boolean> = new EventEmitter<boolean>(false);
  @Output() openSidebar: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() sidebarExpanded?: EventEmitter<void>;
  @Input() chatName: string = '';
  @Input() isLarge: boolean = false;
  @Input() showChat: boolean = false;
  @Input() minimalMode: boolean = false;
  docDrawer: any;
  inputValue: string = '';
  loggedUser: User | undefined;

  sendingMsg: boolean = false;
  uploadDocsDrawerRef: any;
  uploadDocsModalRef: any;

  createdDocuments: File[] = [];

  users: User[] = [];
  createdMentions = new Map<string, string>(); // Almacena el ID y nombre completo

  isMentioning: boolean = false;

  isChatReady$=new BehaviorSubject<boolean>(false);

  constructor(
    private drawerService: NzDrawerService,
    private ps: ProjectUtilsService,
    public authService: AuthService,
    private communicationService: CommunicationService,
    private screenService: ScreenService,
    private partnerService: PartnerService,
    private modalService: NzModalService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
    public gs: GlobalValuesService,
  ) {
    authService.loggedUser
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.loggedUser = value);

    this.getUsers();
    this.isChatReady$.next(true)

  }

  ngOnInit() {
    this.route.queryParams.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      if (params['openChat'] === 'true') {
        this.showChat = true;
        //remover parámetro después de usarlo
        this.router.navigate([], {
          queryParams: { openChat: null },
          queryParamsHandling: 'merge'
        });
      }
    });

    if (this.sidebarExpanded) {
      this.sidebarExpanded.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.showChat = true;
        this.showUnreadMessages();
        this.cdr.detectChanges();
      });
    }
  }
  private destroy$ = new Subject<void>();

  ngOnDestroy() {
    this.docDrawer?.close();
    this.uploadDocsDrawerRef?.close();
    this.uploadDocsModalRef?.close();

    this.destroy$.next();
    this.destroy$.complete();
  }

  private _chat: Chat | undefined;

  get chat(): Chat | undefined {
    return this._chat;
  }

  @Input()
  set chat(value: Chat | undefined) {
    this._chat = value;
    this._chat?.chatMessages?.forEach(m => m.readers = m.readers || [])
    if (value && value.chatMessages?.length > 0) {
      // Calcula la cantidad de mensajes no leídos
      this.unreadedMessages = (value.chatMessages || []).filter(m => !m.readers?.map(r => r.userId).includes(this.loggedUser!.id)).length;
    } else {
      this.unreadedMessages = 0;
      this.unreadIndex = undefined;
    }
  }

  openChatSidebar(){
    this.openSidebar.emit(true);
    this.minimalMode = false;
    this.cdr.detectChanges();
  }

  toggleChat(): void {
    this.minimalMode&&(this.minimalMode=false);
    this.showChat = !this.showChat;
    this.showUnreadMessages();
  }
  showUnreadMessages(): void {
    if (this.showChat) {
      // Calcula la cantidad de mensajes no leídos
      this.instantMessagesUnread = (this.chat!.chatMessages || []).filter(m => !m.readers?.map(r => r.userId).includes(this.loggedUser!.id)).length;
      // Calcula el índice del primer mensaje no leído
      const idx = (this.chat!.chatMessages || []).findIndex(m => !m.readers?.map(r => r.userId).includes(this.loggedUser!.id));
      this.unreadIndex = idx !== -1 ? idx : undefined;

      this.cdr.detectChanges();

      // Espera a que la vista se actualice y luego hace scroll
      setTimeout(() => {
        this.scrollToPosition();
      }, 5);
    }
  }

  send(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.sendingMsg = true;

    let rawMessage = this.inputValue;


    // Reemplazar menciones visibles por formato crudo
    this.createdMentions.forEach((rawValue, displayName) => {
      rawMessage = rawMessage.replace(displayName, rawValue);
    });


    const newMsg: ChatMessageCreate = {
      message: rawMessage.replace(/@(?!\d+_)/g, ''), // Elimina @ no válidos,
      type: this.getMsgType(),
      chatId: this.chat!.id,
      userId: this.loggedUser!.id,
      files: this.createdDocuments
    };

    this.communicationService.createChatMessage(newMsg)
      .pipe(finalize(() => {
        this.sendingMsg = false;
        this.createdDocuments = [];
      }))
      .subscribe({
        next: (rsp) => {
          this.chat!.chatMessages = this.chat!.chatMessages || [];

          rsp.data.readers = [
            {
              id: 0,
              userId: this.loggedUser!.id,
              chatMessageId: rsp.data.id,
            }
          ]

          this.chat!.chatMessages.push(rsp.data);
          this.inputValue = '';
          this.cdr.detectChanges();

          // Usar requestAnimationFrame para sincronizar con el ciclo de renderizado
          requestAnimationFrame(() => {
            this.scrollToBottom();
          });
        },
        error: (e) => console.error(e),
      });
  }

  openDocDrawer(doc: Document): void {
    this.docDrawer = this.drawerService.create({
      nzWidth: 635,
      nzClosable: false,
      nzMask: false,
      nzContent: DocDrawerComponent,
      nzData: {
        document: doc,
      }
    });

    this.isDocDrawerOpen.emit(true)

    this.docDrawer.afterClose.subscribe(() => {
      this.isDocDrawerOpen.emit(false)
    });
  }

  markAsRead(msg: ChatMessage): void {
    // Si el mensaje no ha sido leido por el usuario se incluye y se reevaluan la cantidad de mensajes no leidos
    // Si ya ha sido leido no hace nada
    const readers = msg.readers?.map(r => r.userId) || [];
    // Simula la peticion http de actualizar los mensajes leidos
    if (!readers?.includes(this.loggedUser!.id)) {

      this.communicationService.createMessageReader(msg.id, this.loggedUser!.id).subscribe({
        next: (rsp) => {
          msg.readers ? msg.readers.push(rsp.data) : msg.readers = [rsp.data];
          this.chat?.chatMessages.map(m => m.id === msg.id ? msg : m);
          // Simula la logica dedetectar mensajes no leidos
          this.unreadedMessages = this.chat!.chatMessages?.length > 0
            ? this.chat!.chatMessages.filter(m => !m.readers?.map(r => r.userId).includes(this.loggedUser!.id)).length : 0;
          this.cdr.detectChanges();
        },
        error: (e) => console.error(e),
      });
    }
  }

  private scrollToBottom(): void {
    if (this.messageContainer?.nativeElement) {
      const element = this.messageContainer.nativeElement;
      element.scroll({
        top: element.scrollHeight,
        behavior: 'auto' // 'smooth' para animación
      });
    }
  }

  private scrollToPosition(): void {
    setTimeout(() => {
      const container: HTMLElement = this.messageContainer.nativeElement;
      if (this.unreadIndex !== undefined && this.dividerEl) {
        const dividerEl: HTMLElement = this.dividerEl.nativeElement;
        // Calcula la posición del divider respecto al contenedor
        const dividerOffset = dividerEl.offsetTop;
        // Para centrar el divider en el contenedor:
        const containerCenter = container.clientHeight / 2;
        container.scrollTop = dividerOffset - containerCenter;
      } else {
        // Si no existe divider, hace scroll hasta el final
        container.scrollTop = container.scrollHeight;
      }
    }, 100)
  }

  openUploadDocsModal(): void {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    }).pipe(take(1)).subscribe(({isMobile, isMedium}) => {
      if (isMobile) {
        this.uploadDocsDrawerRef = this.drawerService.create({
          nzClosable: false,
          nzMask: false,
          nzHeight: '98%',
          nzPlacement: 'bottom',
          nzContent: DocModalComponent,
          nzData: {
            docModalType: 'upload',
            onlyReturnFiles: true,
          }
        });

      } else {
        this.uploadDocsModalRef = this.modalService.create({
          nzFooter: null,
          nzContent: DocModalComponent,
          nzClosable: false,
          nzAutofocus: null,
          nzCentered: true,
          nzClassName: 'mobile-drawerlike',
          nzWidth: 'auto'
        });

        this.uploadDocsModalRef.afterOpen.pipe(take(1)).subscribe(() => {
          this.uploadDocsModalRef.getContentComponent().proccessFilesEvent.pipe(take(1)).subscribe((files: File[]) => {
            this.createdDocuments = files;
          });
        });

        this.uploadDocsModalRef.getContentComponent().docModalType = 'upload';
        this.uploadDocsModalRef.getContentComponent().onlyReturnFiles = true;
      }
    });
  }

  getMsgType(): 'file' | 'text' | 'image' | 'multiple' {
    if (this.createdDocuments.length === 0) return 'text';

    let hasImages = false;
    let hasFiles = false;

    for (const document of this.createdDocuments) {
      const isImage = document.type.startsWith('image/');
      const isFile = !isImage;

      if (isImage) hasImages = true;
      if (isFile) hasFiles = true;

      // Salir del ciclo tan pronto como detectemos tipo múltiple
      if (hasImages && hasFiles) {
        return 'multiple';
      }
    }

    return hasImages ? 'image' : 'file';
  }

  getUsers() {
    const usersParams: GetParams = {
      startRow: 0,
      endRow: 100,
      sortDirection: 'asc',
      sortProperty: 'id'
    }
    this.partnerService.getUser(usersParams).subscribe({
      next: (rsp) => this.users = rsp.data.rows,
      error: (e) => console.error(e),
    })
  }

  valueWith = (data: User): string => data.partner!.name;

  highlightMentions(text: string): SafeHtml {
    // Regex que detecta '@' seguido de cualquier texto que no contenga '_' y termina con '_'
    const mentionRegex = /@([^_]+)_/g;

    const highlighted = escapeHtml(text).replace(mentionRegex, (_, content) => {
      const cleanMention = `@${content.trim()}`;
      return `<span style="color: #1890ff; font-weight: 600;">${cleanMention}</span>`;
    });

    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  // mentionSelectionChange(value: any) {
  //   const insertedText = `@${value.partner.id}-${value.partner.name}_ `;
  //
  //   // Reemplazar solo la parte del nombre que el usuario empezó a escribir
  //   const currentInput = this.inputValue;
  //   const lastAtPos = currentInput.lastIndexOf('@');
  //   const partialText = currentInput.substring(lastAtPos + 1);
  //
  //   this.inputValue = currentInput.replace(`@${partialText}`, insertedText);
  //   this.isMentioning = false;
  // }
  mentionSelectionChange(value: any) {
    const displayName = `@${value.partner.name} ${value.partner.surName}`;
    const rawValue = `@${value.partner.id}_`;

    // Reemplazar texto parcial con nombre legible
    const currentInput = this.inputValue;
    const lastAtPos = currentInput.lastIndexOf('@');
    const partialText = currentInput.substring(lastAtPos + 1);

    this.inputValue = currentInput.replace(
      `@${partialText}`,
      `${displayName} `
    );

    // Guardar mapeo para envío
    this.createdMentions.set(displayName, rawValue);
    this.isMentioning = false;
  }

  mentionSearchChange() {
    this.isMentioning = true;
  }

    protected readonly isExpirationAlert = isExpirationAlert;

  getDocumentTypeName(documentTypeId: number): string | undefined {
    return  (this.gs.documentTypes$?.value || []).find(dt => dt.id === documentTypeId)?.name;
  }
}

